/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-material.css';
import 'ag-grid-enterprise';
import 'components/layouts/Account/AccountsGrid.scss';
import 'components/layouts/Performance/RateOfReturnBreakdownGrid.scss';

import { isEmpty } from 'lodash';
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';

import {
  calculateRowHeight,
  getRowClassRules,
  chooseDefs,
  ViewAllBar,
  getContextMenuItems,
  processCellForClipboard,
  BENCHMARK_CLASS,
  SUB_HEADER_CLASS
} from 'services/TableUtils';
import {
  ACTIVE_PLUS_CLASS,
  isAdviceProductClass,
  isCollegeSavingsProductClass
} from 'services/Products';
import { OWNED_LABEL, VIEW_ONLY_LABEL } from 'services/Filters';

const RateOfReturnBreakdownGrid = ({
  isDesktopLayout,
  rateOfReturnSummary,
  maxInitialRowsToShow,
  frameworkComponents,
  performanceFilter,
  columnDefs,
  columnDefsRetirementOrder,
  defaultColDef,
  autoGroupColumnDef,
  rasBenchmark,
  productPerformance,
  aggregatedProductPerformance,
  has529Only,
  shouldUseAggregatedPerformance,
  showMultipleActivePlus,
  sweepMap
}) => {
  const { filter } = performanceFilter;
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [showRows, setShowRows] = useState(true);
  const [printMode, setPrintMode] = useState(false);
  const currentShowRows = useRef(true);
  const firstLoad = useRef(true);
  const managementSelected = useRef(filter.label);
  const accountSelected = useRef(filter.label);
  const scrollRef = useRef(null);
  const prevLayout = useRef(isDesktopLayout);

  window.addEventListener('beforeprint', () => {
    setPrintMode(true);
  });

  window.addEventListener('afterprint', () => {
    setPrintMode(false);
  });

  const setExpanded = () => setShowRows(!showRows);

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api);
    setGridColumnApi(columnApi);

    columnApi && columnApi.autoSizeAllColumns();
  };

  const getRowHeight = ({ node, data, api }) => {
    if (node && node.detail) {
      const allDetailRowHeight =
        (data.positions.length - 1) * api.getSizesForCurrentTheme().rowHeight;
      const gridSizes = api.getSizesForCurrentTheme();
      return allDetailRowHeight + gridSizes.headerHeight - 7;
    } else if (node && node.group) {
      return (
        api.getSizesForCurrentTheme().rowHeight +
        (node.field === 'typeAndOwners' ? node.key.length / 2 : 0)
      );
    } else if (data) {
      return calculateRowHeight(data, api, isDesktopLayout, sweepMap) + 32;
    }
  };

  const getRowClass = ({ node }) => {
    const { group, field, data } = node;

    if (data && data.accountDescriptor === 'Model Benchmark') {
      return BENCHMARK_CLASS;
    }

    if (shouldUseAggregatedPerformance && field === 'productClass') {
      return SUB_HEADER_CLASS;
    }

    return getRowClassRules(group, field, accountSelected.current);
  };

  const groupRowAggNodes = (nodes) => {
    nodes.forEach((node) => {
      if (node.group) {
        return node.aggData;
      }
      return node.data;
    });
  };
  // Updates "View All Accounts" showing or hiding
  useEffect(() => {
    const rowsToShow = gridApi
      ? showRows
        ? maxInitialRowsToShow
        : gridApi.getDisplayedRowCount()
      : maxInitialRowsToShow;
    gridApi && gridApi.paginationSetPageSize(rowsToShow);

    if (
      firstLoad &&
      !firstLoad.current &&
      currentShowRows.current !== showRows
    ) {
      const pos = showRows ? scrollRef.current.offsetTop : window.pageYOffset;
      gridApi && gridApi.refreshClientSideRowModel();
      window.scrollTo(0, pos);
      currentShowRows.current = showRows;
    }
  }, [gridApi, maxInitialRowsToShow, showRows]);

  // updates filters on table
  useEffect(() => {
    if (
      !firstLoad.current &&
      !printMode &&
      (managementSelected.current !== filter.label ||
        accountSelected.current !== filter.label)
    ) {
      managementSelected.current = filter.label;
      accountSelected.current = filter.label;
      gridApi && gridApi.onFilterChanged();
    } else if (firstLoad.current && !isEmpty(filter)) {
      firstLoad.current = false;
    }
  }, [gridApi, filter, maxInitialRowsToShow, printMode]);

  // populates group rows with appropriate product prr data
  useEffect(() => {
    gridApi &&
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        const fillProductRow =
          node.key !== ACTIVE_PLUS_CLASS || !showMultipleActivePlus;
        if (
          node.group &&
          node.field === 'productClass' &&
          !isEmpty(productPerformance) &&
          fillProductRow
        ) {
          const prodPerf = productPerformance.find((product) => {
            return (
              product.productClass === node.key ||
              (isAdviceProductClass(product.productClass) &&
                isAdviceProductClass(node.key)) ||
              (isCollegeSavingsProductClass(product.productClass) &&
                isCollegeSavingsProductClass(node.key))
            );
          });
          const filteringByOwned =
            filter.label === OWNED_LABEL || filter.label === VIEW_ONLY_LABEL;

          node.group = false;
          if (filteringByOwned) {
            node.setAggData({});
          } else {
            !isEmpty(prodPerf) && node.setAggData(prodPerf);
          }
          node.group = true;
        } else if (
          node.group &&
          node.field === 'rasBenchmarkRow' &&
          !isEmpty(rasBenchmark)
        ) {
          // setAggData only runs if node.group is false
          node.group = false;
          const benchmark = rasBenchmark[0];
          node.setAggData(benchmark);
          node.group = true;
        } else if (
          node.group &&
          node.field === 'personalInvestment' &&
          !isEmpty(productPerformance)
        ) {
          node.group = false;
          const prodPerf = aggregatedProductPerformance.find(
            (product) => product.productClass === 'personalInvestment'
          );
          !isEmpty(prodPerf) && node.setAggData(prodPerf);
          node.group = true;
        }
      });
  }, [
    showRows,
    gridApi,
    gridColumnApi,
    productPerformance,
    aggregatedProductPerformance,
    rasBenchmark,
    filter,
    showMultipleActivePlus
  ]);

  // redraws rows on view change
  useEffect(() => {
    if (prevLayout.current !== isDesktopLayout) {
      gridApi && gridApi.redrawRows();
      prevLayout.current = isDesktopLayout;
    }
  }, [isDesktopLayout, gridApi]);

  // resize autoColumn based on print mode
  useEffect(() => {
    if (printMode) {
      gridColumnApi && gridColumnApi.sizeColumnsToFit(1175);
    } else {
      gridColumnApi && gridColumnApi.setColumnWidth('ag-Grid-AutoColumn', 340);
      gridApi && gridApi.sizeColumnsToFit();
    }
  }, [printMode, gridColumnApi, gridApi]);

  return (
    <Fragment>
      { isEmpty(rateOfReturnSummary) ?
        (<div className="activity-summary-breakdown-no-rows-container">
          <span className="activity-summary-breakdown-no-rows-text">No Rows To Show</span>
        </div>
      ):
      <div
        className="ag-theme-material pure-u-1 rate-of-return-grid"
        ref={scrollRef}>
        <AgGridReact
          columnDefs={chooseDefs(
            columnDefs,
            columnDefsRetirementOrder,
            managementSelected.current,
            accountSelected.current,
            '',
            has529Only,
            shouldUseAggregatedPerformance
          )}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          rowData={rateOfReturnSummary}
          frameworkComponents={frameworkComponents}
          getRowHeight={getRowHeight}
          getRowClass={getRowClass}
          onGridReady={onGridReady}
          processCellForClipboard={processCellForClipboard}
          getContextMenuItems={getContextMenuItems}
          groupRowAggNodes={groupRowAggNodes}
          enableRangeSelection
          copyHeadersToClipboard
          groupSuppressBlankHeader
          suppressAggFuncInHeader
          suppressColumnVirtualisation
          groupDefaultExpanded={-1}
          headerHeight={48}
          pagination
          paginateChildRows
          suppressPaginationPanel
          animateRows
          suppressRowTransform
          skipHeaderOnAutoSize={printMode}
          domLayout="autoHeight"
        />
      </div>
      }    
      {gridApi && gridApi.paginationGetRowCount() > maxInitialRowsToShow && (
        <ViewAllBar expanded={showRows} setExpanded={setExpanded}>
          {showRows ? 'View All Accounts' : 'View Less Accounts'}
        </ViewAllBar>
      )}
    </Fragment>
  );
};

RateOfReturnBreakdownGrid.defaultProps = {
  rateOfReturnSummary: []
};

export default RateOfReturnBreakdownGrid;
